/*!
 * Copyright © 2025. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Pipe, PipeTransform } from '@angular/core';
import { EnumUtilities } from '../../../../utils/enum.utilities';
import { DeviceHealthIssueCategory, EPlotType } from '../../../core/models';

@Pipe({
  name: 'deviceHealthIssueTooltip',
  standalone: true
})
export class DeviceHealthIssueTooltip implements PipeTransform {
  transform(value: string | null, deviceType: EPlotType): string | null {
    value = value?.toUpperCase();
    if (!value || !EnumUtilities.isValidEnumValue(value, DeviceHealthIssueCategory)) {
      return null;
    }

    return this.mapToLocalizedErrorString(value, deviceType);
  }

  private mapToLocalizedErrorString(input: DeviceHealthIssueCategory, deviceType: EPlotType): string {
    let localizedString: string;

    switch (deviceType) {
      case EPlotType.vehicle:
        localizedString = this.getVehicleLocalizedStrings(input);
        break;

      case EPlotType.poweredAsset:
      case EPlotType.asset:
        localizedString = this.getAssetLocalizedStrings(input);
        break;

      default:
        localizedString = this.getVehicleLocalizedStrings(input);
        break;
    }
    return localizedString;
  }

  private getVehicleLocalizedStrings(input: DeviceHealthIssueCategory): string {
    let localizedString: string;
    switch (input) {
      case DeviceHealthIssueCategory.Critical:
        localizedString = $localize`:@@S_HEALTH_STATUS_CRITICAL_TOOLTIP:One or more devices in this vehicle cannot operate as intended because its functions are impaired or it is offline.`;
        break;
      case DeviceHealthIssueCategory.Major:
        localizedString = $localize`:@@S_HEALTH_STATUS_MAJOR_TOOLTIP:One or more devices in this vehicle has degraded performance which affects its ability to send data to Reveal.`;
        break;
      case DeviceHealthIssueCategory.Moderate:
        localizedString = $localize`:@@S_HEALTH_STATUS_MODERATE_TOOLTIP:One or more devices in this vehicle has some performance issues that may compromise the quality of the data sent to Reveal.`;
        break;
    }
    return localizedString;
  }

  private getAssetLocalizedStrings(input: DeviceHealthIssueCategory): string {
    let localizedString: string;
    switch (input) {
      case DeviceHealthIssueCategory.Critical:
        localizedString = $localize`:@@S_HEALTH_STATUS_CRITICAL_ASSETS_TOOLTIP:The device in this asset cannot operate as intended because its functions are impaired or it is offline.`;
        break;
      case DeviceHealthIssueCategory.Major:
        localizedString = $localize`:@@S_HEALTH_STATUS_MAJOR_ASSETS_TOOLTIP:The device in this asset has degraded performance which affects its ability to send data to Reveal.`;
        break;
      case DeviceHealthIssueCategory.Moderate:
        localizedString = $localize`:@@S_HEALTH_STATUS_MODERATE_ASSETS_TOOLTIP:The device in this asset has some performance issues that may compromise the quality of the data sent to Reveal.`;
        break;
    }
    return localizedString;
  }
}
